// Project
import {
  DRAFT_REMOVE_TO_ADDRESSEE,
  DRAFT_TO_ADDRESSEES_UPDATE_FAILURE,
  DRAFT_TO_ADDRESSEES_UPDATE_START,
  DRAFT_TO_ADDRESSEES_UPDATE_SUCCESS,
  DRAFT_TO_CHANGED,
  DRAFT_TO_WORKING_ADDRESS_IN_PROGRESS_CHANGE
} from "../../constants/ActionTypes";
import * as applicationConfig from "../../config/application";
import containsDelimiter from "./util/containsDelimiter";
import convertAddressToAddressee from "./util/convertAddressToAddressee";
import initialState from "../../config/initialState";
import isApiStatusCodeError from "./util/isApiStatusCodeError";
import lastIndexOfRegex from "../../util/lastIndexOfRegex";
import parseDelimitedAddressString from "./util/parseDelimitedAddressString";
import uniqBy from "lodash/fp/uniqBy";

/**
 * Update the state with the changed to addressees.
 *
 * @param {Object} state - Current application state.
 * @param {Object} action - Action object.
 * @return {Object} Updated application state.
 */
function removeToAddressee(state, action) {
  const checkAddressee = value => value.address !== action.toAddresseeToRemove;

  return {
    ...state,
    draftView: {
      ...state.draftView,
      to: {
        ...state.draftView.to,
        workingAddressees: state.draftView.to.workingAddressees.filter(checkAddressee)
      }
    }
  };
}

/**
 * Update the state with the changed to addressees.
 *
 * @param {Object} state - Current application state.
 * @param {Object} action - Action object.
 * @return {Object} Updated application state.
 */
function toAddressChanged(state, action) {
  const addressesToBeUpdated = parseDelimitedAddressString(action.workingToAddress)
    .filter(Boolean)
    .map(address => convertAddressToAddressee(address));

  return {
    ...state,
    draftView: {
      ...state.draftView,
      to: {
        ...state.draftView.to,
        workingAddress: "",
        workingAddressees: uniqBy("address", state.draftView.to.workingAddressees.concat(addressesToBeUpdated))
      }
    }
  };
}

/* eslint-disable max-lines-per-function */
/**
 * Update the state with the changed to.
 *
 * @param {Object} state - Current application state.
 * @param {Object} action - Action object.
 * @return {Object} Updated application state.
 */
function toWorkingAddressInProgressChange(state, action) {
  const delimiterRegex = new RegExp(applicationConfig.draftView.addressDelimiters, "u");
  const lastDelimiterIndex = lastIndexOfRegex(action.workingToAddress, delimiterRegex);

  if (containsDelimiter(action.workingToAddress)) {
    const addressesToBeUpdated = parseDelimitedAddressString(
      action.workingToAddress.substring(0, lastDelimiterIndex)
    ).filter(Boolean)
      .map(address => convertAddressToAddressee(address));

    return {
      ...state,
      draftView: {
        ...state.draftView,
        to: {
          ...state.draftView.to,
          workingAddress: action.workingToAddress.substring(lastDelimiterIndex + 1),
          workingAddressees: uniqBy("address", state.draftView.to.workingAddressees.concat(addressesToBeUpdated))
        }
      }
    };
  }

  return {
    ...state,
    draftView: {
      ...state.draftView,
      to: {
        ...state.draftView.to,
        workingAddress: action.workingToAddress.substring(lastDelimiterIndex + 1)
      }
    }
  };
}

/**
 * Add update error notification to application state.
 *
 * @param {Object} state - Application state.
 * @param {Object} action - Update failure action.
 * @return {Object} Updated application state.
 */
function toUpdateFailure(state, action) {
  const invalidEmailCode = "400";
  const containsInvalid = isApiStatusCodeError(invalidEmailCode, action.error);

  return {
    ...state,
    draftView: {
      ...state.draftView,
      draftAutosaving: false,
      to: {
        ...state.draftView.to,
        containsInvalid: containsInvalid
      }
    }
  };
}

/**
 * Update the draft autosave flag in application state.
 *
 * @param {String} state - Application stated.
 * @param {Object} action - Update start action.
 * @return {Object} Updated application state.
 */
function toUpdateStart(state, action) {
  return { ...state,
    draftView: {
      ...state.draftView,
      draftAutosaving: action.draftAutosaving
    } };
}

/**
 * Update application state with to from API.
 *
 * @param {Object} state - Application state.
 * @param {Object} action - To updated action.
 * @return {Objet} Updated application state.
 */
function toAddressUpdateSuccess(state, action) {
  return {
    ...state,
    draftView: {
      ...state.draftView,
      draftAutosaving: false,
      to: {
        ...state.draftView.to,
        cachedAddressees: action.toAddressees,
        containsInvalid: false,
        workingAddressees: action.toAddressees
      }
    }
  };
}

/**
 * Combine the draft message reducers.
 *
 * @param {Object} state - Current application state.
 * @param {Object} action - Action type.
 * @return {Object} Updated application state.
 */
export function draftToReducer(state = initialState, action) {
  if (action && action.type) {
    switch (action.type) {
      case DRAFT_REMOVE_TO_ADDRESSEE: return removeToAddressee(state, action);

      case DRAFT_TO_CHANGED: return toAddressChanged(state, action);

      case DRAFT_TO_WORKING_ADDRESS_IN_PROGRESS_CHANGE: return toWorkingAddressInProgressChange(state, action);

      case DRAFT_TO_ADDRESSEES_UPDATE_FAILURE: return toUpdateFailure(state, action);

      case DRAFT_TO_ADDRESSEES_UPDATE_SUCCESS: return toAddressUpdateSuccess(state, action);

      case DRAFT_TO_ADDRESSEES_UPDATE_START: return toUpdateStart(state, action);

      default: return state;
    }
  } else {
    return state;
  }
}
